import { Injectable } from '@angular/core';

/**
 * Service for sanitizing filenames to prevent XSS and path traversal attacks.
 * Removes dangerous characters that could be exploited when filenames are displayed.
 */
@Injectable({ providedIn: 'root' })
export class FilenameSanitizerService {
  private readonly _htmlCharsRegex = /[<>:"|?*]/g;
  private readonly _pathSeparatorRegex = /[/\\]/g;
  private readonly _multipleDotsRegex = /\.{2,}/g;
  private readonly _multipleSpacesRegex = /\s{2,}/g;
  private readonly _maxLength = 255;

  /**
   * Sanitizes a filename by removing dangerous characters.
   * Defense against XSS, path traversal, and extension hiding attacks.
   *
   * @param filename - Original filename to sanitize
   * @returns Sanitized filename safe for storage and display
   */
  public sanitize(filename: string): string {
    let sanitized = filename;

    // Remove path separators (prevents directory traversal: ../../../etc/passwd)
    sanitized = sanitized.replace(this._pathSeparatorRegex, '_');

    // Remove HTML/XML dangerous characters (prevents XSS: <script>alert('XSS')</script>.pdf)
    sanitized = sanitized.replace(this._htmlCharsRegex, '');

    // Remove control characters (ASCII 0-31) dynamically to avoid eslint no-control-regex
    sanitized = this._removeControlCharacters(sanitized);

    // Replace multiple dots (prevents extension hiding: file.txt....exe)
    sanitized = sanitized.replace(this._multipleDotsRegex, '.');

    // Collapse multiple spaces
    sanitized = sanitized.replace(this._multipleSpacesRegex, ' ');

    // Trim whitespace
    sanitized = sanitized.trim();

    // Truncate to max length (keep extension)
    if (sanitized.length > this._maxLength) {
      const extension = this._getExtension(sanitized);
      const nameWithoutExt = sanitized.slice(0, sanitized.length - extension.length);
      const maxNameLength = this._maxLength - extension.length;
      sanitized = nameWithoutExt.slice(0, maxNameLength) + extension;
    }

    // Fallback if sanitization resulted in empty string
    if (!sanitized || sanitized === '.') {
      sanitized = `file_${Date.now()}`;
    }

    return sanitized;
  }

  private _removeControlCharacters(str: string): string {
    // Remove ASCII control characters (0-31) character by character
    // Using String.fromCharCode avoids eslint no-control-regex warning
    return str.replace(new RegExp(`[${String.fromCharCode(0)}-${String.fromCharCode(31)}]`, 'g'), '');
  }

  private _getExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    return lastDot === -1 ? '' : filename.slice(lastDot);
  }
}
